import { Box } from '@chakra-ui/react';
import { EditorContent, useEditor } from '@tiptap/react';
import { Code } from 'constants/keyboard';
import { useEventListener } from 'hooks/useEventListener';
import { useUnmountCallback } from 'hooks/useUnmountCallback';
import isEqual from 'lodash.isequal';
import { MouseEvent, ReactElement, memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import { ReadOnlyWidgetBase, WidgetBase, WidgetToolbar, useEditor as useInfographEditor, useWidget } from 'widgets/sdk';

import { findFirstFontFamily } from '../common/TextBasedWidgets.helpers';
import { WidgetState } from '../common/TextBasedWidgets.types';
import '../common/prosemirror.css';
import { TableEditor } from './TableEditor';
import { DEFAULT_CARET_COLOR, extensions } from './TableWidget.config';
import { handlePaste, isPositionWithinCurrentSelection } from './TableWidget.helpers';
import { EditorStateWithTableColumn, TableWidgetBaseProps, TableWidgetData } from './TableWidget.types';
import { generateTableContextMenuItems } from './TableWidgetContextMenu.helpers';
import { TableWidgetKeyboardShortcuts } from './TableWidgetKeyboardShortcuts';
import { markStore } from './extensions/EmptyTextStyle';
import { DATA_ID_ATTRIBUTE } from './extensions/tableExtension.config';
import { TableWidgetToolbar } from './toolbar/TableWidgetToolbar';
import { useTableBoundingBox } from './useTableBoundingBox';

export const TableWidget = (): ReactElement => {
  const [widgetState, setNewWidgetState] = useState(WidgetState.Default);
  const { updateWidget, widgetId, proseMirrorData } = useWidget<TableWidgetData>();
  const { boundingBox } = useInfographEditor();
  const [editorId, setEditorId] = useState(0);

  const isEditable = widgetState === WidgetState.Edit;
  const editor = useEditor(
    {
      extensions,
      editorProps: {
        attributes: { [DATA_ID_ATTRIBUTE]: widgetId },
        handleDOMEvents: {
          // Prevent text drag and drop
          drop: (_, e: Event) => {
            e.preventDefault();
            return false;
          },
        },
        handlePaste,
      },
      content: proseMirrorData,
    },
    [editorId],
  );

  const dispatchUpdateWidget = useCallback(
    (options?: Partial<TableWidgetData>) => {
      const newProseMirrorData = editor?.getJSON();
      if (!newProseMirrorData || !editor) return;
      if (isEqual(newProseMirrorData, proseMirrorData)) return;

      const heightPx = editor.view.dom.clientHeight ?? 0;
      const widthPx = editor.view.dom.clientWidth ?? 0;

      updateWidget({ proseMirrorData: newProseMirrorData, widthPx, heightPx, ...options });
      // update the bounding box rect since the height can be updated
      setTimeout(() => boundingBox.updateRect(), 0);
    },
    [editor, updateWidget, proseMirrorData, boundingBox],
  );

  // reset the editor / history when the font family is changed from the apply font
  const destroyAndCreateNewEditor = () => {
    if (!editor) return;

    editor.destroy();
    setEditorId((editorId) => editorId + 1);
  };
  useEventListener('applyTheme', destroyAndCreateNewEditor);

  return (
    <>
      <WidgetToolbar>
        {editor && (
          <TableWidgetToolbar dispatchUpdateWidget={dispatchUpdateWidget} editor={editor} isEditable={isEditable} />
        )}
      </WidgetToolbar>
      <TableWidgetBase
        widgetState={widgetState}
        setNewWidgetState={setNewWidgetState}
        editor={editor}
        dispatchUpdateWidget={dispatchUpdateWidget}
        caretColor={editor?.getAttributes('textStyle')?.color || DEFAULT_CARET_COLOR}
      />
    </>
  );
};

const TableWidgetBase = memo(
  ({ setNewWidgetState, widgetState, editor, dispatchUpdateWidget, caretColor }: TableWidgetBaseProps) => {
    const {
      boundingBox,
      setWidgetFocus,
      enableEditorKeyboardShortcuts,
      disableEditorKeyboardShortcuts,
      isWidgetFocused,
      hideContextMenu,
      setContextMenuItems,
      openContextMenu,
    } = useInfographEditor();
    const { proseMirrorData, isWidgetSelected } = useWidget<TableWidgetData>();

    // keep these in refs to avoid running the undo/redo effect when these change
    // (we only want to run the undo/redo effect when the proseMirrorData has changed)
    const isWidgetSelectedRef = useRef(isWidgetSelected);
    const setWidgetStateRef = useRef(setNewWidgetState);

    const resetColumnResize = useCallback(() => {
      // if the column resize handle is active reset it
      const state = editor?.view.state as EditorStateWithTableColumn;
      if (state) {
        const columnResizeActive = state.tableColumnResizing$?.activeHandle !== -1;
        if (!columnResizeActive || !state.tableColumnResizing$) return;

        state.tableColumnResizing$.activeHandle = -1;
        editor?.view.updateState(state);
      }
    }, [editor]);

    const setWidgetState = useCallback(
      (newWidgetState: WidgetState, clickPosition?: { left: number; top: number }) => {
        setNewWidgetState(newWidgetState);

        const isActive = newWidgetState === WidgetState.Active;
        const isDefault = newWidgetState === WidgetState.Default;
        const isEditable = newWidgetState === WidgetState.Edit;

        if (isActive || isDefault) {
          // enable keyboard shortcuts when the widget is in active state
          enableEditorKeyboardShortcuts();

          // reset the text editor state
          if (!editor?.isDestroyed) {
            editor?.commands.blur();
            editor?.commands.setTextSelection(0);
            editor?.setEditable(false);
            resetColumnResize();
          }
        }

        // When the widget is in edit state the editor is focused
        // Reset the focus to the widget when state changes from edit -> active for widget keyboard shortcuts to work
        if (isActive && !isWidgetFocused()) setWidgetFocus();

        if (isEditable) {
          // disable keyboard shortcuts when the widget is in edit state
          // The duplicate shortcut is still allowed
          disableEditorKeyboardShortcuts({ allowedKeyboardKeys: [Code.D] });

          // focus the editor if its not focused
          if (editor && !editor.isFocused && !editor.isDestroyed) {
            editor.setEditable(true);

            // where the cursor should be placed in the table editor
            let selectionPos = 0;
            if (clickPosition) {
              const clickedTableEl = document.elementFromPoint(clickPosition.left, clickPosition.top);

              // if the click is on a table node
              // find the first paragraph node and place the cursor at the end of it
              // (selection can't be set to table nodes)
              if (clickedTableEl && (clickedTableEl.tagName === 'TD' || clickedTableEl.tagName === 'TH')) {
                // find first p in the cell (paragraph node)
                const p = clickedTableEl.querySelector('p');
                if (p) {
                  // get the position of the paragraph node
                  const posAtDom = editor.view.posAtDOM(p, -1);
                  // set selection to end of paragraph node in the cell
                  const node = editor.view.state.doc.nodeAt(posAtDom);
                  selectionPos = posAtDom + (node?.nodeSize || 0);
                }
              } else {
                const pos = editor.view.posAtCoords(clickPosition);
                if (pos && typeof pos.pos === 'number') selectionPos = pos.pos;
              }
            }

            editor.commands.focus(selectionPos);
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [enableEditorKeyboardShortcuts, disableEditorKeyboardShortcuts, isWidgetFocused, setWidgetFocus, editor],
    );

    // sync the ref values
    setWidgetStateRef.current = setWidgetState;
    isWidgetSelectedRef.current = isWidgetSelected;

    const onClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        // If click target (target) matches widget container (currentTarget), keep it in active state
        // otherwise the text content was clicked so enter edit state
        if (widgetState === WidgetState.Active && e.target !== e.currentTarget) {
          const pos = { left: e.clientX, top: e.clientY };
          setWidgetState(WidgetState.Edit, pos);
        }
        if (widgetState === WidgetState.Default) setWidgetState(WidgetState.Active);
      },
      [setWidgetState, widgetState],
    );

    const onBlur = useCallback(() => {
      dispatchUpdateWidget();
      if (!isWidgetSelected) editor?.commands.blur();
    }, [editor, dispatchUpdateWidget, isWidgetSelected]);

    // If the widget is not selected anymore, reset the widget state to default
    useEffect(() => {
      if (!isWidgetSelected && widgetState !== WidgetState.Default) {
        setWidgetState(WidgetState.Default);
        // in case there's any marks from a cell deletion clear it when the widget is deselected
        markStore.clearMarks();
      }
      // a widget can be selected without going through onClick (e.g. keyboard tab, drag area)
      // a selected text widget should never be in default state
      if (isWidgetSelected && widgetState === WidgetState.Default) setWidgetState(WidgetState.Active);
    }, [isWidgetSelected, widgetState, setWidgetState]);

    // UNDO/REDO effect
    // Sync editor content with proseMirrorData that has changed externally (e.g. from undo/redo)
    // For large document, it may require more optimization later on
    useEffect(() => {
      const shouldResetEditorContent = editor && proseMirrorData && !isEqual(editor.getJSON(), proseMirrorData);
      if (shouldResetEditorContent && !editor.isDestroyed) {
        // if the column resize is active reset it
        // keeping it active will cause the editor to crash with setContent
        resetColumnResize();
        editor.commands.setContent(proseMirrorData);

        // reset the widget state to active since the selection is lost when the editor content is reset
        if (isWidgetSelectedRef.current) {
          setWidgetStateRef.current(WidgetState.Active);
          // update the rect since the height can be updated from the new content
          setTimeout(() => boundingBox.updateRect(), 0);
        }
      }
    }, [boundingBox, editor, proseMirrorData, resetColumnResize]);

    // if the widget is deleted ensure that the keyboard shortcuts are enabled again
    useEffect(() => () => enableEditorKeyboardShortcuts(), [enableEditorKeyboardShortcuts]);

    useUnmountCallback(() => {
      if (isWidgetSelectedRef.current) dispatchUpdateWidget();
    });

    // Show default context menu if in edit state
    const onContextMenu = useCallback(
      (e: MouseEvent<HTMLElement>) => {
        if (widgetState !== WidgetState.Edit || !editor) return;

        // Prevent default context menu + generic widget context menu
        e.stopPropagation();
        e.preventDefault();
        hideContextMenu();

        const clickedInsideSelection = isPositionWithinCurrentSelection(editor, { top: e.clientY, left: e.clientX });
        const items = generateTableContextMenuItems(editor, dispatchUpdateWidget, !clickedInsideSelection);
        setContextMenuItems(items);

        openContextMenu({ x: e.pageX, y: e.pageY });
      },
      [hideContextMenu, widgetState, setContextMenuItems, openContextMenu, editor, dispatchUpdateWidget],
    );

    const {
      customOnResizeStart,
      customOnResize,
      customOnResizeEnd,
      customOnRotateStart,
      customOnDrag,
      customOnDragEnd,
    } = useTableBoundingBox({
      setWidgetState,
      editor,
      isWidgetSelected,
      dispatchUpdateWidget,
    });

    return (
      <WidgetBase
        onClick={onClick}
        onContextMenu={onContextMenu}
        onResizeStart={customOnResizeStart}
        onResizeEnd={customOnResizeEnd}
        onResize={customOnResize}
        onRotateStart={customOnRotateStart}
        onDrag={customOnDrag}
        onDragEnd={customOnDragEnd}
      >
        {isWidgetSelected && editor && (
          <TableWidgetKeyboardShortcuts setWidgetState={setWidgetState} widgetState={widgetState} editor={editor} />
        )}
        <TableEditor widgetState={widgetState} editor={editor} onBlur={onBlur} caretColor={caretColor} />
      </WidgetBase>
    );
  },
);
TableWidgetBase.displayName = 'TableWidgetBase';

export const ReadOnlyTableWidget = (): ReactElement => {
  const { proseMirrorData, renderType } = useWidget<TableWidgetData>();
  const disableTabbability = renderType === ReadOnlyRenderType.ThumbnailReadOnly;

  /**
   * Note: There is possible tiptap bug that not update table column with editable flag set to false at initial load
   * @see https://github.com/ueberdosis/tiptap/blob/be4cde0e026a7668f99f85a9248e215873dbd9f0/packages/extension-table/src/table.ts#L233
   */
  const editor = useEditor(
    {
      extensions,
      editorProps: {
        // To avoid `tabbability` on tables from the thumbnail in the page slide view
        // that interfere with the page tab order
        attributes: { tabindex: '-1' },
        handleDOMEvents: {
          // Prevent text drag and drop
          drop: (_, e: Event) => {
            e.preventDefault();
            return false;
          },
          mousedown: (_, e: Event) => {
            // Prevent all mouse click
            e.preventDefault();
            e.stopImmediatePropagation();
            return false;
          },
        },
      },
      content: proseMirrorData,
    },
    [proseMirrorData],
  );

  // Disable edit
  useEffect(() => editor?.setEditable(false), [editor]);

  // Disable tabbing for links in the PM
  useLayoutEffect(() => {
    if (!editor) return;

    if (disableTabbability) {
      // find all link children of element and set tabindex to -1
      // the custom link option does not work for this for tables
      const links = editor.view.dom.querySelectorAll('a');
      links.forEach((link) => link.setAttribute('tabindex', '-1'));
    }
  }, [editor, disableTabbability]);

  const preventMouseEvent = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    return false;
  };

  const fontFamily = findFirstFontFamily(editor);

  return (
    <ReadOnlyWidgetBase disableSingleSelect>
      <Box
        h='fit-content'
        w='100%'
        position='relative'
        className={disableTabbability ? undefined : 'allow-link-clicks'}
        onMouseDown={preventMouseEvent}
        // for line-height consistency when exporting to pdf - the parent div should have the same font family as the editor
        // obs: there are probably edge-cases here with multiple fonts in the same text widget
        fontFamily={fontFamily}
      >
        <EditorContent editor={editor} />
      </Box>
    </ReadOnlyWidgetBase>
  );
};
